<template>
    <div>
        <FullPageLoader :isLoading="loading || previewLoading || confirmingSave" />

        <CreateCreditInvoiceButton
            ref="CreateCreditInvoiceButton"
            :customerId="contract?.customer"
            :number="String(contractId || addendum?.id || 0)"
            :contractId="contractId"
            :products="creditModalProducts"
            :deposit_products="creditModalDepositProducts"
            :extraProducts="[]"
            :endDate="creditModalEndDate"
            :hideTheButton="true"
            :scenarioType="'addendum_remove'"
        />

        <DiscountModal
            :is-open="showDiscountModal"
            :product-price="currentDiscountProductPrice"
            :existing-discounts="currentDiscountProductDiscounts"
            :restrict-to-all-invoices="true"
            @close="showDiscountModal = false"
            @apply="applyDiscount"
            @remove="removeDiscount"
        />

        <ContractsRecordProducts
            ref="boxPicker"
            type="boxes"
            mode="new"
            :record="productsRecord"
            :startDate="record.effective_date"
            :onlyModal="true"
            :organisation="contract?.organisation"
            @passProducts="handleBoxSelection"
        />
        <ContractsRecordProducts
            ref="productPicker"
            type="products"
            mode="new"
            :record="productsRecord"
            :onlyModal="true"
            :organisation="contract?.organisation"
            @passProducts="handleProductSelection"
        />

        <div class="row">
            <div class="column medium-4 text-right float-right">
                <b-button
                    tag="router-link"
                    :to="{ name: contractId ? 'ContractsRecordView' : 'ContractsOverview', params: contractId ? { id: contractId } : {} }"
                    type="is-dark"
                >
                    {{ contractId ? 'Terug naar contract' : 'Terug' }}
                </b-button>
            </div>
            <div class="column medium-8">
                <h1>{{ type === 'new' ? 'Contract aanpassen' : `Contractaanpassing ${addendumReference}` }}</h1>
            </div>
        </div>

        <div class="row" v-if="type === 'new'">
            <div class="column">
                <div class="card">
                    <div class="card-content">
                        <h3>Algemeen</h3>
                        <b-field label="Ingangsdatum wijzigingen">
                            <template #label>
                                Ingangsdatum wijzigingen
                                <b-tooltip label="Vanaf deze datum worden ingeplande facturen geannuleerd en opnieuw aangemaakt op basis van deze contractaanpassing." position="is-right">
                                    <b-icon icon="info-circle" size="is-small" />
                                </b-tooltip>
                            </template>
                            <b-datepicker
                                placeholder="Klik om te selecteren"
                                :nearby-selectable-month-days="true"
                                icon="calendar-alt"
                                icon-pack="fas"
                                :min-date="moment(new Date()).startOf('day').toDate()"
                                v-model="record.effective_date"
                            />
                        </b-field>

                        <b-field>
                            <b-checkbox v-model="record.require_signing" :true-value="1" :false-value="0">
                                Eerst een addendum-pdf laten ondertekenen door de klant, voordat de contractwijziging wordt verwerkt.
                                <b-tooltip label="Na het toevoegen wordt een contractaanpassing PDF (addendum) aangemaakt en verstuurd naar de klant om digitaal te ondertekenen.">
                                    <b-icon icon="info-circle" size="is-small" />
                                </b-tooltip>
                            </b-checkbox>
                        </b-field>

                        <transition name="slide-fade">
                            <b-field v-if="record.require_signing === 1" label="Ondertekenen vóór">
                                <template #label>
                                    Ondertekenen vóór
                                    <b-tooltip label="Als de klant vóór deze datum niet ondertekent, wordt de contractaanpassing niet verwerkt en worden eerder geannuleerde facturen automatisch hersteld." position="is-right">
                                        <b-icon icon="info-circle" size="is-small" />
                                    </b-tooltip>
                                </template>
                                <b-datepicker
                                    placeholder="Klik om te selecteren"
                                    :nearby-selectable-month-days="true"
                                    icon="calendar-alt"
                                    icon-pack="fas"
                                    :min-date="moment(new Date()).startOf('day').toDate()"
                                    v-model="record.sign_before_date"
                                />
                            </b-field>
                        </transition>

                        <ContractBillingSettingsFields
                            v-model="record"
                            :disableIncasso="!user.organisation_incasso"
                            invoiceFreqLabel="Facturatie frequentie"
                            paymentTypeLabel="Betaalmethode"
                            :showWeeklyPriceNotice="true"
                        />

                        <!-- <b-notification
                            v-if="record.require_signing === 1"
                            type="is-info is-light"
                            :closable="false"
                        >
                        </b-notification> -->

                        <div class="row">
                            <div class="column">
                                <b-field>
                                    <template #label>
                                        Opmerking (optioneel)
                                        <b-tooltip :label="record.require_signing === 1 ? 'Opmerkingen worden weergegeven in de e-mail en de contractaanpassing PDF (addendum).' : 'Opmerkingen worden weergegeven in de e-mail.'">
                                            <b-icon icon="info-circle" size="is-small" />
                                        </b-tooltip>
                                    </template>
                                    <b-input v-model="record.notes" type="textarea" class="is-fullwidth" rows="3"></b-input>
                                </b-field>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="row" v-if="type === 'new'">
            <div class="column">
                <div class="card">
                    <div class="card-content">
                        <h3>Bestaande producten in contract</h3>
                        <b-notification type="is-warning is-light" :closable="false" v-if="currentContractProducts.some(row => isOnceLocked(row))">
                            <strong>Eenmalige producten</strong> kunnen niet aangepast of verwijderd worden.
                        </b-notification>
                        <b-table :data="currentContractProducts" :striped="true" :mobile-cards="true">
                            <template>
                                <b-table-column v-slot="props" field="type" label="Type" width="120">
                                    {{ props.row.type === 'box' ? 'Box' : 'Product' }}
                                </b-table-column>
                                <b-table-column v-slot="props" field="name" label="Nummer / naam">
                                    <b-input
                                        :value="getEditableValue(props.row, 'name')"
                                        :disabled="isOnceLocked(props.row) || removeContractProducts.includes(props.row.id)"
                                        @input="setEditableValue(props.row, 'name', $event)"
                                    />
                                </b-table-column>
                                <b-table-column v-slot="props" field="amount" label="Aantal" width="130">
                                    <b-input
                                        v-if="props.row.type === 'product'"
                                        type="number"
                                        min="0.01"
                                        step="1"
                                        :value="getEditableValue(props.row, 'amount')"
                                        :disabled="isOnceLocked(props.row) || removeContractProducts.includes(props.row.id)"
                                        @input="setEditableValue(props.row, 'amount', $event)"
                                    />
                                    <span v-else>1</span>
                                </b-table-column>
                                <b-table-column v-slot="props" field="price" label="Prijs" width="140">
                                    <b-input
                                        type="text"
                                        inputmode="decimal"
                                        :value="getEditableValue(props.row, 'price')"
                                        :disabled="isOnceLocked(props.row) || removeContractProducts.includes(props.row.id)"
                                        @input="setEditableValue(props.row, 'price', $event)"
                                        @blur="setEditableValue(props.row, 'price', formatDecimal(getEditableValue(props.row, 'price')))"
                                    />
                                </b-table-column>
                                <b-table-column v-slot="props" field="btw" label="BTW %" width="120">
                                    <b-input
                                        type="text"
                                        inputmode="decimal"
                                        :value="getEditableValue(props.row, 'btw')"
                                        :disabled="isOnceLocked(props.row) || removeContractProducts.includes(props.row.id)"
                                        @input="setEditableValue(props.row, 'btw', $event)"
                                        @blur="setEditableValue(props.row, 'btw', formatDecimal(getEditableValue(props.row, 'btw')))"
                                    />
                                </b-table-column>
                                <b-table-column v-slot="props" field="discounts" label="Korting" width="150">
                                    <b-button
                                        type="is-info"
                                        icon-left="percent"
                                        :outlined="!hasDiscountForCurrentProduct(props.row)"
                                        :disabled="isOnceLocked(props.row) || removeContractProducts.includes(props.row.id)"
                                        @click="openDiscountModalForCurrentProduct(props.row)"
                                    >
                                        {{ getDiscountDisplay(getEditableDiscountsForCurrentProduct(props.row)) }}
                                    </b-button>
                                </b-table-column>
                                <b-table-column v-slot="props" field="remove" label="Verwijderen" width="170">
                                    <b-button
                                        :type="removeContractProducts.includes(props.row.id) ? 'is-danger' : 'is-dark'"
                                        :disabled="isOnceLocked(props.row)"
                                        @click="toggleRemoveContractProduct(props.row.id)"
                                    >
                                        {{ removeContractProducts.includes(props.row.id) ? 'Annuleren' : 'Verwijderen' }}
                                    </b-button>
                                </b-table-column>
                            </template>
                        </b-table>
                    </div>
                </div>
            </div>
        </div>

        <div class="row" v-if="type === 'new'">
            <div class="column">
                <div class="card">
                    <div class="card-content">
                        <div class="is-flex is-justify-content-space-between is-align-items-center">
                            <h3 class="m-b-0">Boxen toevoegen</h3>
                            <b-button type="is-primary" @click="openBoxModal">Box zoeken</b-button>
                        </div>
                        <b-notification
                            v-if="unavailableAddBoxes.length > 0"
                            type="is-danger is-light"
                            :closable="false"
                            class="m-t-10"
                        >
                            Eén of meerdere geselecteerde boxen zijn niet beschikbaar op de gekozen ingangsdatum.
                        </b-notification>
                        <b-table :data="addBoxes" :striped="true" :mobile-cards="true" class="mt-3">
                            <template>
                                <b-table-column v-slot="props" field="type" label="Type" width="120">
                                    Box
                                </b-table-column>
                                <b-table-column v-slot="props" field="number" label="Nummer / naam">
                                    <b-input v-model="props.row.name" />
                                    <p
                                        v-if="isBoxUnavailableForEffectiveDate(props.row)"
                                        class="help is-danger m-t-5"
                                    >
                                        Deze box is niet beschikbaar op de gekozen ingangsdatum.
                                    </p>
                                </b-table-column>
                                <b-table-column v-slot="props" field="price" label="Prijs" width="140">
                                    <b-input
                                        type="text"
                                        inputmode="decimal"
                                        v-model="props.row.price"
                                        @blur="formatNewDecimal(props.row, 'price')"
                                    />
                                </b-table-column>
                                <b-table-column v-slot="props" field="btw" label="BTW %" width="120">
                                    <b-input
                                        type="text"
                                        inputmode="decimal"
                                        v-model="props.row.btw"
                                        @blur="formatNewDecimal(props.row, 'btw')"
                                    />
                                </b-table-column>
                                <b-table-column v-slot="props" field="deposit" label="Borg" width="140">
                                    <b-input
                                        type="text"
                                        inputmode="decimal"
                                        v-model="props.row.deposit"
                                        @blur="formatNewDecimal(props.row, 'deposit')"
                                    />
                                </b-table-column>
                                <b-table-column v-slot="props" field="discounts" label="Korting" width="150">
                                    <b-button
                                        type="is-info"
                                        icon-left="percent"
                                        :outlined="!(props.row.discounts && props.row.discounts.length)"
                                        @click="openDiscountModalForAddedRow(props.row, 'add_box')"
                                    >
                                        {{ getDiscountDisplay(props.row.discounts) }}
                                    </b-button>
                                </b-table-column>
                                <b-table-column v-slot="props" numeric width="100">
                                    <b-button type="is-danger" @click="removeAddedBox(props.row.id)">Verwijderen</b-button>
                                </b-table-column>
                            </template>
                            <template #empty>
                                <section class="section">
                                    <div class="content has-text-grey has-text-centered">
                                        <p class="margin0">Nog geen boxen toegevoegd.</p>
                                    </div>
                                </section>
                            </template>
                        </b-table>
                    </div>
                </div>
            </div>
        </div>

        <div class="row" v-if="type === 'new'">
            <div class="column">
                <div class="card">
                    <div class="card-content">
                        <div class="is-flex is-justify-content-space-between is-align-items-center is-flex-wrap-wrap">
                            <h3 class="m-b-0">Producten toevoegen</h3>
                            <div class="is-flex">
                                <b-button type="is-dark" @click="addManualProduct">Handmatig product toevoegen</b-button>
                                <b-button type="is-primary" @click="openProductModal" class="marginleft10">Product zoeken</b-button>
                            </div>
                        </div>
                        <b-table :data="addProducts" :striped="true" :mobile-cards="true" class="mt-3">
                            <template>
                                <b-table-column v-slot="props" field="type" label="Type" width="120">
                                    Product
                                </b-table-column>
                                <b-table-column v-slot="props" field="name" label="Naam">
                                    <b-input v-model="props.row.name" />
                                </b-table-column>
                                <b-table-column v-slot="props" field="amount" label="Aantal" width="130">
                                    <b-input type="number" min="0.01" step="1" v-model="props.row.amount" />
                                </b-table-column>
                                <b-table-column v-slot="props" field="once" label="Eenmalig" width="120">
                                    <b-checkbox v-model="props.row.once" />
                                </b-table-column>
                                <b-table-column v-slot="props" field="price" label="Prijs" width="140">
                                    <b-input
                                        type="text"
                                        inputmode="decimal"
                                        v-model="props.row.price"
                                        @blur="formatNewDecimal(props.row, 'price')"
                                    />
                                </b-table-column>
                                <b-table-column v-slot="props" field="btw" label="BTW %" width="120">
                                    <b-input
                                        type="text"
                                        inputmode="decimal"
                                        v-model="props.row.btw"
                                        @blur="formatNewDecimal(props.row, 'btw')"
                                    />
                                </b-table-column>
                                <b-table-column v-slot="props" field="deposit" label="Borg" width="140">
                                    <b-input
                                        type="text"
                                        inputmode="decimal"
                                        v-model="props.row.deposit"
                                        @blur="formatNewDecimal(props.row, 'deposit')"
                                    />
                                </b-table-column>
                                <b-table-column v-slot="props" field="discounts" label="Korting" width="150">
                                    <b-button
                                        type="is-info"
                                        icon-left="percent"
                                        :outlined="!(props.row.discounts && props.row.discounts.length)"
                                        @click="openDiscountModalForAddedRow(props.row, 'add_product')"
                                    >
                                        {{ getDiscountDisplay(props.row.discounts) }}
                                    </b-button>
                                </b-table-column>
                                <b-table-column v-slot="props" numeric width="100">
                                    <b-button type="is-danger" @click="removeAddedProduct(props.row.id)">Verwijderen</b-button>
                                </b-table-column>
                            </template>
                            <template #empty>
                                <section class="section">
                                    <div class="content has-text-grey has-text-centered">
                                        <p class="margin0">Nog geen producten toegevoegd.</p>
                                    </div>
                                </section>
                            </template>
                        </b-table>
                    </div>
                </div>
            </div>
        </div>

        <div class="row" v-if="type === 'view' && addendum">
            <div class="column">
                <div class="card">
                    <div class="card-content">
                        <!-- <h3>Contractaanpassing {{ addendumReference }}</h3> -->
                        <b-notification v-if="addendum.status === 'pending_signature'" type="is-warning is-light">
                            Deze contractaanpassing wacht op bevestiging.
                            <span v-if="!addendum.file_id"> Het addendum-PDF wordt binnenkort aangemaakt en verstuurd.</span>
                        </b-notification>
                        <b-notification v-if="addendum.status === 'pending_processing'" type="is-info is-light">
                            <span v-if="isFuturePendingWithoutSigning">
                                Deze contractaanpassing wordt automatisch verwerkt op {{ moment(addendum.effective_date).format('DD-MM-YYYY') }}.
                            </span>
                            <span v-else>
                                Deze contractaanpassing is klaar voor verwerking.
                            </span>
                        </b-notification>
                        <b-notification v-if="addendum.status === 'applied'" type="is-success is-light">
                            Deze contractaanpassing is verwerkt.
                        </b-notification>
                        <b-notification v-if="addendum.status === 'cancelled'" type="is-danger is-light">
                            Deze contractaanpassing is geannuleerd.
                        </b-notification>

                        <div class="row">
                            <div class="column">
                                <b-field label="Ingangsdatum">{{ moment(addendum.effective_date).format('DD-MM-YYYY') }}</b-field>
                            </div>
                            <div class="column">
                                <b-field label="Status">{{ addendumStatusLabel(addendum.status) }}</b-field>
                            </div>
                            <div class="column">
                                <b-field label="Ondertekening verplicht">{{ addendum.require_signing == 1 ? 'Ja' : 'Nee' }}</b-field>
                            </div>
                            <div class="column" v-if="addendum.require_signing == 1 && addendum.sign_before_date">
                                <b-field label="Ondertekenen vóór">{{ moment(addendum.sign_before_date).format('DD-MM-YYYY') }}</b-field>
                            </div>
                        </div>

                        <div class="row">
                            <div class="column">
                                <b-field label="Facturatie frequentie wijziging">
                                    {{ addendum.invoice_freq ? invoiceFreqLabel(addendum.invoice_freq) : 'Ongewijzigd' }}
                                </b-field>
                            </div>
                            <div class="column">
                                <b-field label="Betaalmethode wijziging">
                                    {{ addendum.payment_type ? paymentTypeLabel(addendum.payment_type) : 'Ongewijzigd' }}
                                </b-field>
                            </div>
                        </div>

                        <hr>

                        <b-field label="Opmerking" v-if="addendum.notes">{{ addendum.notes }}</b-field>

                        <hr v-if="addendum.notes">

                        <h3>Wijzigingen</h3>
                        <b-table :data="addendum.items || []" :striped="true" :mobile-cards="true">
                            <template>
                                <b-table-column v-slot="props" field="action" label="Actie" width="140">
                                    {{ itemActionLabel(props.row.action) }}
                                </b-table-column>
                                <b-table-column v-slot="props" field="type" label="Type" width="120">
                                    {{ props.row.type === 'box' ? 'Box' : 'Product' }}
                                </b-table-column>
                                <b-table-column v-slot="props" field="name" label="Nummer / naam">
                                    {{ props.row.name }}
                                </b-table-column>
                                <b-table-column v-slot="props" field="amount" label="Aantal" width="120">
                                    {{ props.row.amount }}
                                </b-table-column>
                                <b-table-column v-slot="props" field="price" label="Prijs" width="130">
                                    &euro; {{ Number(props.row.price || 0).toFixed(2) }}
                                </b-table-column>
                                <b-table-column v-slot="props" field="discounts" label="Korting" width="140">
                                    {{ addendumItemDiscountLabel(props.row) }}
                                </b-table-column>
                            </template>
                        </b-table>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="column">
                <div class="formfooter is-flex is-justify-content-space-between" v-if="type === 'new'">
                    <b-button
                        type="is-light"
                        tag="router-link"
                        :to="{ name: contractId ? 'ContractsRecordView' : 'ContractsOverview', params: contractId ? { id: contractId } : {} }"
                    >
                        Annuleren
                    </b-button>
                    <b-button type="is-success" :disabled="!hasChanges" @click="openPreviewModal">
                        Contractaanpassing toevoegen
                    </b-button>
                </div>
                <div class="formfooter is-flex is-justify-content-space-between is-align-items-center" v-else-if="addendum && ['pending_signature', 'pending_processing'].includes(addendum.status)">
                    <b-button
                        type="is-light"
                        tag="router-link"
                        :to="{ name: contractId ? 'ContractsRecordView' : 'ContractsOverview', params: contractId ? { id: contractId } : {} }"
                    >
                        Terug naar contract
                    </b-button>
                    <div class="is-flex">
                        <b-button
                            type="is-danger"
                            :loading="loading"
                            @click="promptCancelAddendum"
                        >
                            Annuleren
                        </b-button>
                    <b-button
                        v-if="addendum.require_signing == 1 && addendum.status === 'pending_signature'"
                        type="is-warning"
                        :loading="loading"
                        @click="resendSigningEmail"
                    >
                        Ondertekeningsmail opnieuw inplannen
                    </b-button>
                        <b-button
                            v-if="shouldShowProcessButton"
                            type="is-success"
                            :loading="loading"
                            @click="processAddendum"
                        >
                            Verwerken
                        </b-button>
                    </div>
                </div>
            </div>
        </div>

        <b-modal :active.sync="showPreviewModal" :width="980" scroll="keep" can-cancel>
            <div class="modal-card" style="width: auto;">
                <header class="modal-card-head">
                    <p class="modal-card-title">Controleer factuurwijzigingen</p>
                </header>
                <section class="modal-card-body" style="border-radius:0;">
                    <b-notification type="is-warning is-light" :closable="false" v-if="previewWarnings.length > 0">
                        <div v-for="(warning, index) in previewWarnings" :key="`warn-${index}`">{{ warning }}</div>
                    </b-notification>
                    <b-notification type="is-info is-light" :closable="false" v-if="previewItems.length > 0">
                        De onderstaande geplande facturen worden geannuleerd en opnieuw ingepland op basis van de contractaanpassing<span v-show="record?.require_signing">&nbsp;na ondertekening</span>.
                    </b-notification>
                    <b-notification type="is-info is-light" :closable="false" v-if="previewDiscountRows.length > 0">
                        Korting(en) worden meegenomen in de herberekening.
                    </b-notification>
                    <b-table v-if="previewDiscountRows.length > 0" :data="previewDiscountRows" :striped="true" :mobile-cards="true" class="m-b-15">
                        <template>
                            <b-table-column v-slot="props" field="type" label="Type" width="120">
                                {{ props.row.type }}
                            </b-table-column>
                            <b-table-column v-slot="props" field="name" label="Nummer / naam">
                                {{ props.row.name }}
                            </b-table-column>
                            <b-table-column v-slot="props" field="discount" label="Korting">
                                {{ props.row.discount }}
                            </b-table-column>
                        </template>
                    </b-table>
                    <b-notification type="is-info is-light" :closable="false" v-if="previewItems.length === 0">
                        Er worden geen geplande facturen gewijzigd.<br>
                        Nieuwe facturen worden aangemaakt op basis van de contractaanpassing.
                    </b-notification>
                    <div v-if="previewItems.length > 0">
                        <b-table :data="previewItems" :striped="true" :mobile-cards="true">
                            <template style="overflow-x: hidden;">
                                <b-table-column v-slot="props" field="invoice_number" label="Factuurnummer" width="180">
                                    <router-link
                                        :to="{ name: 'InvoicesRecord', params: { type: 'recent', id: (props.row.invoice_id || props.row.invoice) } }"
                                        target="_blank"
                                    >
                                        <strong>
                                            {{ props.row.invoice_number }}
                                            <b-tooltip label="Bekijk factuur in nieuw tabblad" position="is-right">
                                                <b-icon icon="external-link-alt" size="is-small" />
                                            </b-tooltip>
                                        </strong>
                                    </router-link>                               </b-table-column>
                                <b-table-column v-slot="props" field="invoice_date" label="Factuurdatum" width="170">
                                    {{ moment(props.row.invoice_date).format('DD-MM-YYYY') }}
                                </b-table-column>
                                <b-table-column v-slot="props" field="old_total" label="Huidig totaal" width="170">
                                    <span>&euro; {{ Number(props.row.old_total || 0).toFixed(2) }}</span>
                                </b-table-column>
                                <b-table-column v-slot="props" field="new_total" label="Nieuw totaal" width="170">
                                    <span>&euro; {{ Number(props.row.new_total || 0).toFixed(2) }}</span>
                                </b-table-column>
                                <b-table-column v-slot="props" field="difference" label="Verschil" width="140">
                                    <span :class="props.row.difference >= 0 ? 'has-text-success' : 'has-text-danger'">
                                        {{ props.row.difference >= 0 ? '+' : '-' }} &euro; {{ Number(Math.abs(props.row.difference || 0)).toFixed(2) }}
                                    </span>
                                </b-table-column>
                            </template>
                        </b-table>
                    </div>
                </section>
                <footer class="modal-card-foot is-justify-content-space-between">
                    <b-button type="is-light" @click="showPreviewModal = false">Annuleren</b-button>
                    <b-button type="is-success" :loading="confirmingSave" @click="confirmSaveAddendum">
                        Bevestigen en opslaan
                    </b-button>
                </footer>
            </div>
        </b-modal>

        <b-modal :active.sync="showProgressModal" :width="500" :can-cancel="false">
            <div class="modal-card" style="width: auto;">
                <header class="modal-card-head">
                    <p class="modal-card-title">Facturen genereren</p>
                </header>

                <section class="modal-card-body" style="overflow: hidden;border-radius:0;">
                    <div class="has-text-centered">
                        <p class="mb-4">
                            <strong>Factuur {{ currentCronjobIndex }} van {{ totalCronjobs }}</strong>
                        </p>

                        <b-progress
                            :value="progressPercentage"
                            size="is-medium"
                            :type="progressType"
                            show-value
                        >
                            {{ progressPercentage }}%
                        </b-progress>

                        <p class="mt-4 has-text-grey" v-if="processingCronjobs">
                            <small>Je kunt deze pagina verlaten.<br>De facturen worden dan op de achtergrond verder verwerkt.</small>
                        </p>

                        <p class="mt-4 has-text-success" v-if="!processingCronjobs && cronjobsCompleted">
                            Alle facturen zijn succesvol gegenereerd.
                        </p>

                        <p class="mt-4 has-text-danger" v-if="cronjobErrors.length > 0">
                            {{ cronjobErrors.length }} factuur(en) konden niet worden gegenereerd.
                        </p>
                    </div>
                </section>

                <footer class="modal-card-foot is-justify-content-space-between">
                    <div></div>
                    <b-button
                        type="is-success"
                        @click="finishProcessing"
                    >
                        Doorgaan
                    </b-button>
                </footer>
            </div>
        </b-modal>
    </div>
</template>

<script>
import ContractsRecordProducts from '@/vue/contracts/ContractsRecordProducts'
import toastMixins from '@/mixins/toastMixins'
import CreateCreditInvoiceButton from '@/vue/creditinvoice/CreateCreditInvoiceButton'
import ContractBillingSettingsFields from '@/vue/contracts/components/ContractBillingSettingsFields'
import DiscountModal from '@/vue/discounts/DiscountModal'

export default {
    name: 'ContractsAddendumsRecord',
    components: {
        ContractsRecordProducts,
        CreateCreditInvoiceButton,
        ContractBillingSettingsFields,
        DiscountModal
    },
    mixins: [toastMixins],
    data() {
        return {
            loading: false,
            previewLoading: false,
            confirmingSave: false,
            type: 'new',
            contractId: null,
            contract: null,
            addendum: null,
            contractAddendums: [],
            currentContractProducts: [],
            removeContractProducts: [],
            addBoxes: [],
            addProducts: [],
            updateContractProducts: {},
            showPreviewModal: false,
            previewItems: [],
            previewWarnings: [],
            showProgressModal: false,
            cronjobIds: [],
            currentCronjobIndex: 0,
            totalCronjobs: 0,
            processingCronjobs: false,
            cronjobsCompleted: false,
            cronjobErrors: [],
            cronjobAfterAction: null,
            savePayload: null,
            creditModalProducts: [],
            creditModalDepositProducts: [],
            creditModalEndDate: null,
            showDiscountModal: false,
            currentDiscountProductPrice: 0,
            currentDiscountProductDiscounts: [],
            currentDiscountTarget: null,
            record: {
                effective_date: moment(new Date()).startOf('day').toDate(),
                require_signing: 0,
                sign_before_date: moment(new Date()).startOf('day').add(14, 'days').toDate(),
                notes: '',
                invoice_freq: 'month',
                payment_type: 'manual'
            }
        }
    },
    computed: {
        user() {
            return this.$store.state.user
        },
        productsRecord() {
            return {
                step1: {
                    invoice_freq: this.contract?.invoice_freq || 'month'
                }
            }
        },
        hasChanges() {
            const payload = this.buildPayload()
            const hasSettingChanges = payload.invoice_freq !== null || payload.payment_type !== null
            return this.unavailableAddBoxes.length === 0
                && (
                    payload.remove_contract_products.length > 0
                || payload.update_contract_products.length > 0
                || payload.add_boxes.length > 0
                || payload.add_products.length > 0
                || hasSettingChanges
                )
        },
        unavailableAddBoxes() {
            return (this.addBoxes || []).filter(box => this.isBoxUnavailableForEffectiveDate(box))
        },
        contractNumber() {
            if (!this.contract) {
                return null
            }
            return this.contract.number || this.contract.id
        },
        addendumReference() {
            if (!this.addendum) {
                return ''
            }

            const contractNumber = this.contractNumber || this.addendum.contract || this.contractId || ''
            const sequence = this.getAddendumSequence(this.addendum)
            return `${contractNumber}-${String(sequence).padStart(2, '0')}`
        },
        progressPercentage() {
            if (this.totalCronjobs === 0) return 0
            return Math.round((this.currentCronjobIndex / this.totalCronjobs) * 100)
        },
        progressType() {
            if (this.cronjobErrors.length > 0) return 'is-warning'
            if (this.cronjobsCompleted) return 'is-success'
            return 'is-primary'
        },
        previewDiscountRows() {
            if (!this.savePayload) {
                return []
            }

            const rows = []
            const appendRows = (items, typeLabel) => {
                ;(items || []).forEach((item) => {
                    const discounts = this.normalizeDiscounts(item?.discounts || [])
                    if (discounts.length === 0) {
                        return
                    }

                    discounts.forEach((discount) => {
                        rows.push({
                            type: typeLabel,
                            name: item?.name || '-',
                            discount: discount.type === 'percentage'
                                ? `${Number(discount.value)}%`
                                : `€${Number(discount.value).toFixed(2)}`
                        })
                    })
                })
            }

            appendRows(this.savePayload.update_contract_products, 'Bestaand')
            appendRows(this.savePayload.add_boxes, 'Box')
            appendRows(this.savePayload.add_products, 'Product')

            return rows
        },
        isFuturePendingWithoutSigning() {
            if (!this.addendum || this.addendum.status !== 'pending_processing') {
                return false
            }

            if (Number(this.addendum.require_signing || 0) !== 0) {
                return false
            }

            return moment(this.addendum.effective_date).startOf('day').isAfter(moment().startOf('day'))
        },
        shouldShowProcessButton() {
            return false
        }
    },
    watch: {
        'record.require_signing'(value) {
            if (Number(value) === 1) {
                this.ensureSignBeforeDateDefault()
            }
        },
        'record.effective_date'() {
            this.ensureSignBeforeDateDefault()
        }
    },
    created() {
        if (this.$route.params.id === 'nieuw') {
            this.type = 'new'
            this.contractId = this.$route.query.contract ? Number(this.$route.query.contract) : null
            this.record.require_signing = this.user?.organisation_settings?.require_addendum_signing === 1 ? 1 : 0
            this.ensureSignBeforeDateDefault()
            if (this.contractId) {
                this.fetchContract(this.contractId)
            }
        } else {
            this.type = 'view'
            this.fetchAddendum()
        }
    },
    methods: {
        ensureSignBeforeDateDefault() {
            if (Number(this.record.require_signing || 0) !== 1) {
                this.record.sign_before_date = null
                return
            }

            const minSignDate = moment().startOf('day')
            const defaultSignDate = moment().startOf('day').add(14, 'days')

            if (!this.record.sign_before_date) {
                this.record.sign_before_date = defaultSignDate.toDate()
                return
            }

            const currentSignDate = moment(this.record.sign_before_date).startOf('day')
            if (currentSignDate.isBefore(minSignDate)) {
                this.record.sign_before_date = defaultSignDate.toDate()
            }
        },
        fetchContract(id) {
            this.loading = true
            axios.get(`/api/contract/${id}`, {
                headers: { 'Authorization': 'bearer ' + this.$store.state.authToken }
            }).then((response) => {
                this.contract = response.data
                this.contractId = response.data.id
                this.record.invoice_freq = response.data.invoice_freq || 'month'
                this.record.payment_type = response.data.payment_type || 'manual'
                this.currentContractProducts = (response.data.contract_products || []).map(row => ({
                    ...row,
                    discounts: this.normalizeDiscounts(row.discounts || []),
                    original_discounts_key: this.serializeDiscounts(row.discounts || []),
                    original_name: row.name,
                    original_amount: Number(row.amount || 1),
                    original_price: Number(row.price || 0),
                    original_deposit: Number(row.deposit || 0),
                    original_btw: Number(row.btw || 0)
                }))
            }).catch(() => {
                this.showError('Contract kon niet geladen worden.')
                this.$router.push({ name: 'ContractsOverview' })
            }).then(() => {
                this.loading = false
            })
        },
        fetchAddendum() {
            this.loading = true
            axios.get(`/api/contract/addendums/${this.$route.params.id}`, {
                headers: { 'Authorization': 'bearer ' + this.$store.state.authToken }
            }).then((response) => {
                this.addendum = response.data
                this.contract = response.data.contract
                this.contractId = response.data.contract?.id || null
                this.fetchContractAddendumsForReference()
            }).catch(() => {
                this.showError('Contractaanpassing kon niet geladen worden.')
                this.$router.push({ name: 'ContractsOverview' })
            }).then(() => {
                this.loading = false
            })
        },
        fetchContractAddendumsForReference() {
            if (!this.contractId) {
                return
            }

            axios.get(`/api/contract/${this.contractId}/addendums`, {
                headers: { 'Authorization': 'bearer ' + this.$store.state.authToken }
            }).then((response) => {
                this.contractAddendums = response.data || []
            }).catch(() => {
                this.contractAddendums = []
            })
        },
        isOnceLocked(row) {
            return Number(row.once || 0) === 1
        },
        getEditableValue(row, field) {
            const byId = this.updateContractProducts[row.id] || {}
            if (Object.prototype.hasOwnProperty.call(byId, field)) {
                return byId[field]
            }
            if (field === 'amount') {
                return row.type === 'box' ? 1 : Number(row.amount || 1)
            }
            if (field === 'price' || field === 'deposit' || field === 'btw') {
                return this.formatDecimal(row[field] || 0)
            }
            return row[field]
        },
        setEditableValue(row, field, value) {
            if (!this.updateContractProducts[row.id]) {
                this.$set(this.updateContractProducts, row.id, {})
            }
            if (field === 'amount' || field === 'price' || field === 'deposit' || field === 'btw') {
                this.$set(this.updateContractProducts[row.id], field, this.parseDecimal(value))
            } else {
                this.$set(this.updateContractProducts[row.id], field, value)
            }
        },
        normalizeDiscounts(discounts) {
            const rows = Array.isArray(discounts) ? discounts : []
            return rows
                .map((discount) => {
                    const type = discount?.type === 'fixed' ? 'fixed' : 'percentage'
                    const value = this.parseDecimal(discount?.value || 0)
                    const discountPreset = discount?.discount_preset ? Number(discount.discount_preset) : null
                    const invoice = discount?.invoice || discount?.invoice_number
                    return {
                        discount_preset: Number.isInteger(discountPreset) && discountPreset > 0 ? discountPreset : null,
                        type,
                        value,
                        description: discount?.description ? String(discount.description) : null,
                        invoice: invoice ? Number(invoice) : null,
                        apply_to_all: !!discount?.apply_to_all
                    }
                })
                .filter(discount => discount.value >= 0)
        },
        serializeDiscounts(discounts) {
            const normalized = this.normalizeDiscounts(discounts).map((discount) => ({
                ...discount,
                value: Number(this.parseDecimal(discount.value).toFixed(2))
            }))
            normalized.sort((a, b) => {
                const aAll = a.apply_to_all ? 0 : 1
                const bAll = b.apply_to_all ? 0 : 1
                if (aAll !== bAll) return aAll - bAll
                return Number(a.invoice || 0) - Number(b.invoice || 0)
            })
            return JSON.stringify(normalized)
        },
        getEditableDiscountsForCurrentProduct(row) {
            const byId = this.updateContractProducts[row.id] || {}
            if (Object.prototype.hasOwnProperty.call(byId, 'discounts')) {
                return this.normalizeDiscounts(byId.discounts)
            }
            return this.normalizeDiscounts(row.discounts || [])
        },
        hasDiscountForCurrentProduct(row) {
            return this.getEditableDiscountsForCurrentProduct(row).length > 0
        },
        getDiscountDisplay(discounts) {
            const normalized = this.normalizeDiscounts(discounts)
            if (normalized.length === 0) {
                return 'Korting'
            }

            if (normalized.length === 1) {
                const discount = normalized[0]
                if (discount.type === 'percentage') {
                    return `${Number(discount.value)}%`
                }
                return `€${Number(discount.value).toFixed(2)}`
            }

            return `${normalized.length} kortingen`
        },
        openDiscountModalForCurrentProduct(row) {
            this.currentDiscountTarget = {
                kind: 'current_contract_product',
                id: Number(row.id)
            }
            this.currentDiscountProductPrice = this.parseDecimal(this.getEditableValue(row, 'price') || 0)
            this.currentDiscountProductDiscounts = this.getEditableDiscountsForCurrentProduct(row)
            this.showDiscountModal = true
        },
        openDiscountModalForAddedRow(row, kind) {
            this.currentDiscountTarget = {
                kind,
                id: Number(row.id)
            }
            this.currentDiscountProductPrice = this.parseDecimal(row.price || 0)
            this.currentDiscountProductDiscounts = this.normalizeDiscounts(row.discounts || [])
            this.showDiscountModal = true
        },
        applyDiscount(discountsData) {
            const normalizedDiscounts = this.normalizeDiscounts(discountsData?.discounts || [])
            const target = this.currentDiscountTarget
            if (!target) {
                this.currentDiscountTarget = null
                this.showDiscountModal = false
                return
            }

            if (target.kind === 'current_contract_product') {
                if (!this.updateContractProducts[target.id]) {
                    this.$set(this.updateContractProducts, target.id, {})
                }
                this.$set(this.updateContractProducts[target.id], 'discounts', normalizedDiscounts)
            } else if (target.kind === 'add_box') {
                const row = this.addBoxes.find(item => Number(item.id) === Number(target.id))
                if (row) {
                    this.$set(row, 'discounts', normalizedDiscounts)
                }
            } else if (target.kind === 'add_product') {
                const row = this.addProducts.find(item => Number(item.id) === Number(target.id))
                if (row) {
                    this.$set(row, 'discounts', normalizedDiscounts)
                }
            }

            this.currentDiscountTarget = null
            this.showDiscountModal = false
        },
        removeDiscount() {
            this.applyDiscount({ discounts: [] })
        },
        parseDecimal(value) {
            const normalized = String(value || '').replace(',', '.')
            const parsed = parseFloat(normalized)
            return Number.isFinite(parsed) ? parsed : 0
        },
        formatDecimal(value) {
            return this.parseDecimal(value).toFixed(2)
        },
        formatNewDecimal(row, field) {
            this.$set(row, field, this.formatDecimal(row[field]))
        },
        isBoxUnavailableForEffectiveDate(box) {
            if (!box || !box.available_at || !this.record?.effective_date) {
                return false
            }

            return moment(box.available_at).startOf('day').isAfter(moment(this.record.effective_date).startOf('day'))
        },
        formatBoxDisplayName(box) {
            const number = String(box?.number || box?.name || box?.id || '').trim()
            if (!number) {
                return ''
            }

            return /^box\s/i.test(number) ? number : `Box ${number}`
        },
        extractBackendValidationMessage(error) {
            const errors = error?.response?.data?.errors
            if (!errors || typeof errors !== 'object') {
                return null
            }

            const preferredKeys = [
                'changes',
                'invoice_freq',
                'payment_type',
                'add_boxes',
                'add_products',
                'remove_contract_products',
                'update_contract_products'
            ]

            for (const key of preferredKeys) {
                if (Array.isArray(errors[key]) && errors[key].length > 0) {
                    return errors[key][0]
                }
            }

            const firstKey = Object.keys(errors)[0]
            if (firstKey && Array.isArray(errors[firstKey]) && errors[firstKey].length > 0) {
                return errors[firstKey][0]
            }

            return null
        },
        openBoxModal() {
            this.$refs.boxPicker.showModal(this.addBoxes)
        },
        openProductModal() {
            this.$refs.productPicker.showModal(this.addProducts)
        },
        handleBoxSelection(payload) {
            this.addBoxes = (payload.records || []).map(box => ({
                id: Number(box.id || box.product || 0),
                name: this.formatBoxDisplayName(box),
                number: box.number || null,
                available_at: box.available_at || null,
                price: this.formatDecimal(box.price || 0),
                deposit: this.formatDecimal(box.deposit || 0),
                btw: this.formatDecimal(box.btw || 0),
                discounts: this.normalizeDiscounts(box.discounts || [])
            })).filter(box => box.id > 0)
        },
        handleProductSelection(payload) {
            this.addProducts = (payload.records || []).map(product => ({
                id: Number(product.id || product.product || 0),
                name: product.name || '',
                amount: Number(product.amount || 1),
                once: !!product.once,
                manual: !!product.manual,
                price: this.formatDecimal(product.price || 0),
                deposit: this.formatDecimal(product.deposit || 0),
                btw: this.formatDecimal(product.btw || 0),
                discounts: this.normalizeDiscounts(product.discounts || [])
            })).filter(product => product.id > 0 || product.manual)
        },
        addManualProduct() {
            this.addProducts.push({
                id: Math.floor(100000000 + Math.random() * 900000000),
                manual: true,
                name: '',
                amount: 1,
                once: false,
                price: this.formatDecimal(0),
                deposit: this.formatDecimal(0),
                btw: this.formatDecimal(21),
                discounts: []
            })
        },
        toggleRemoveContractProduct(id) {
            if (this.removeContractProducts.includes(id)) {
                this.removeContractProducts = this.removeContractProducts.filter(rowId => Number(rowId) !== Number(id))
            } else {
                this.removeContractProducts = [...this.removeContractProducts, id]
            }
        },
        removeAddedBox(id) {
            this.addBoxes = this.addBoxes.filter(row => row.id !== id)
            if (this.$refs.boxPicker) {
                this.$refs.boxPicker.removeProduct(Number(id))
            }
        },
        removeAddedProduct(id) {
            this.addProducts = this.addProducts.filter(row => row.id !== id)
        },
        buildPayload() {
            const updates = []
            this.currentContractProducts.forEach(row => {
                if (this.isOnceLocked(row) || this.removeContractProducts.includes(row.id)) {
                    return
                }

                const editedName = String(this.getEditableValue(row, 'name') || '').trim()
                const editedAmount = row.type === 'box' ? 1 : Number(this.getEditableValue(row, 'amount') || 1)
                const editedPrice = this.parseDecimal(this.getEditableValue(row, 'price') || 0)
                const editedBtw = this.parseDecimal(this.getEditableValue(row, 'btw') || 0)
                const editedDiscounts = this.getEditableDiscountsForCurrentProduct(row)
                const editedDiscountsKey = this.serializeDiscounts(editedDiscounts)

                const changed = editedName !== String(row.original_name || '')
                    || (row.type === 'product' && editedAmount !== Number(row.original_amount || 1))
                    || editedPrice !== Number(row.original_price || 0)
                    || editedBtw !== Number(row.original_btw || 0)
                    || editedDiscountsKey !== String(row.original_discounts_key || '[]')

                if (changed) {
                    updates.push({
                        id: row.id,
                        name: editedName,
                        amount: editedAmount,
                        price: editedPrice,
                        deposit: Number(row.original_deposit || 0),
                        btw: editedBtw,
                        discounts: editedDiscounts
                    })
                }
            })

            const removeContractProducts = [...new Set(
                (this.removeContractProducts || [])
                    .map(id => Number(id))
                    .filter(id => Number.isInteger(id) && id > 0)
            )]

            return {
                effective_date: moment(this.record.effective_date).format('YYYY-MM-DD'),
                require_signing: this.record.require_signing === 1,
                sign_before_date: this.record.require_signing === 1 && this.record.sign_before_date
                    ? moment(this.record.sign_before_date).format('YYYY-MM-DD')
                    : null,
                notes: this.record.notes,
                invoice_freq: (this.contract && this.record.invoice_freq !== this.contract.invoice_freq) ? this.record.invoice_freq : null,
                payment_type: (this.contract && this.record.payment_type !== this.contract.payment_type) ? this.record.payment_type : null,
                remove_contract_products: removeContractProducts,
                update_contract_products: updates,
                add_boxes: this.addBoxes.map(row => ({
                    id: Number(row.id || row.product || 0),
                    name: row.name,
                    price: this.parseDecimal(row.price || 0),
                    deposit: this.parseDecimal(row.deposit || 0),
                    btw: this.parseDecimal(row.btw || 0),
                    discounts: this.normalizeDiscounts(row.discounts || [])
                })).filter(row => row.id > 0),
                add_products: this.addProducts.map(row => ({
                    id: row.manual ? null : Number(row.id || row.product || 0),
                    name: row.name,
                    amount: Number(row.amount || 1),
                    once: !!row.once,
                    manual: !!row.manual,
                    price: this.parseDecimal(row.price || 0),
                    deposit: this.parseDecimal(row.deposit || 0),
                    btw: this.parseDecimal(row.btw || 0),
                    discounts: this.normalizeDiscounts(row.discounts || [])
                })).filter(row => (row.manual && String(row.name || '').trim().length > 0) || row.id > 0)
            }
        },
        getRemovedDepositProducts() {
            const selectedIds = new Set((this.removeContractProducts || []).map(id => Number(id)))
            const rows = (this.currentContractProducts || [])
                .filter(row => selectedIds.has(Number(row.id)) && this.parseDecimal(row.deposit || 0) > 0)
                .map(row => ({
                    id: Number(row.product || row.id),
                    product: Number(row.product || row.id),
                    name: row.name,
                    amount: Number(row.amount || 1),
                    price: this.parseDecimal(row.price || 0),
                    deposit: this.parseDecimal(row.deposit || 0),
                    btw: this.parseDecimal(row.btw || 0),
                    type: row.type
                }))
            const seen = new Set()
            return rows.filter((row) => {
                const key = `${row.product}-${row.name}-${row.amount}-${row.deposit}`
                if (seen.has(key)) {
                    return false
                }
                seen.add(key)
                return true
            })
        },
        openCreditModalForRemovedProducts(removedProducts) {
            if (!removedProducts || removedProducts.length === 0) {
                return
            }
            this.creditModalProducts = removedProducts
            this.creditModalDepositProducts = removedProducts
            const effectiveDate = this.addendum?.effective_date || this.record?.effective_date
            this.creditModalEndDate = effectiveDate ? moment(effectiveDate).format('YYYY-MM-DD') : null
            this.$nextTick(() => {
                this.$refs.CreateCreditInvoiceButton?.toggleModal()
            })
        },
        openPreviewModal() {
            if (!this.contractId) {
                this.showError('Geen contract geselecteerd.')
                return
            }
            if (this.unavailableAddBoxes.length > 0) {
                this.showError('Eén of meerdere geselecteerde boxen zijn niet beschikbaar op de gekozen ingangsdatum.')
                return
            }
            const payload = this.buildPayload()
            if (
                payload.remove_contract_products.length === 0
                && payload.update_contract_products.length === 0
                && payload.add_boxes.length === 0
                && payload.add_products.length === 0
                && payload.invoice_freq === null
                && payload.payment_type === null
            ) {
                this.showError('Er zijn geen wijzigingen geselecteerd.')
                return
            }
            this.previewItems = []
            this.previewWarnings = []
            this.previewLoading = true
            axios.post(`/api/contract/${this.contractId}/addendums/preview`, payload, {
                headers: { 'Authorization': 'bearer ' + this.$store.state.authToken }
            }).then((response) => {
                this.previewItems = response.data.items || []
                this.previewWarnings = response.data.warnings || []
                this.savePayload = payload
                this.showPreviewModal = true
            }).catch((error) => {
                const backendError = error?.response?.data?.error
                const backendValidationMessage = this.extractBackendValidationMessage(error)
                const backendErrorMessage = error?.response?.data?.error_message
                if (backendError === 'not-org-admin') {
                    this.showError('Alleen een administrator kan een contractaanpassing maken.')
                } else if (backendValidationMessage) {
                    this.showError(backendValidationMessage)
                } else if (backendError === 'preview-failed' && backendErrorMessage) {
                    this.showError(`Voorbeeld mislukt: ${backendErrorMessage}`)
                } else if (backendError) {
                    this.showError(backendError)
                } else {
                    this.showError('Voorbeeld kon niet geladen worden.')
                }
            }).then(() => {
                this.previewLoading = false
            })
        },
        confirmSaveAddendum() {
            if (!this.savePayload) {
                return
            }
            this.confirmingSave = true
            const removedDepositProducts = this.getRemovedDepositProducts()
            axios.post(`/api/contract/${this.contractId}/addendums`, this.savePayload, {
                headers: { 'Authorization': 'bearer ' + this.$store.state.authToken }
            }).then((response) => {
                const invoiceCronjobIds = response.data.invoice_cronjob_ids || []
                if (response.data.status === 'pending_signature') {
                    this.showMessage('Contractaanpassing opgeslagen. Het addendum wordt binnenkort aangemaakt en verstuurd.')
                    this.$router.push({ name: 'ContractsRecordView', params: { id: this.contractId } })
                    return
                }

                if (response.data.status === 'pending_processing' && response.data.scheduled_processing) {
                    this.showMessage('Contractaanpassing opgeslagen. Verwerking gebeurt automatisch op de ingangsdatum.')
                    if (invoiceCronjobIds.length > 0) {
                        this.showPreviewModal = false
                        this.startCronjobProcessing(invoiceCronjobIds, 'redirect')
                    } else {
                        this.$router.push({ name: 'ContractsRecordView', params: { id: this.contractId } })
                    }
                    return
                }

                const cronjobIds = response.data.cronjob_ids || []
                if (cronjobIds.length > 0) {
                    this.showMessage('Contractaanpassing verwerkt. Facturen worden direct gegenereerd.')
                    this.showPreviewModal = false
                    if (removedDepositProducts.length > 0) {
                        this.startCronjobProcessing(cronjobIds, 'none', false)
                        this.openCreditModalForRemovedProducts(removedDepositProducts)
                    } else {
                        this.startCronjobProcessing(cronjobIds, 'redirect')
                    }
                    return
                }

                this.showMessage('Contractaanpassing verwerkt.')
                if (removedDepositProducts.length > 0) {
                    this.openCreditModalForRemovedProducts(removedDepositProducts)
                } else {
                    this.$router.push({ name: 'ContractsRecordView', params: { id: this.contractId } })
                }
            }).catch((error) => {
                const backendError = error?.response?.data?.error
                const backendValidationMessage = this.extractBackendValidationMessage(error)
                if (backendError === 'not-org-admin') {
                    this.showError('Alleen een administrator kan een contractaanpassing maken.')
                } else if (backendError === 'active-addendum-exists') {
                    this.showError('Er staat al een open contractaanpassing klaar voor dit contract.')
                } else if (backendValidationMessage) {
                    this.showError(backendValidationMessage)
                } else if (backendError) {
                    this.showError(backendError)
                } else {
                    this.showError('Contractaanpassing opslaan is mislukt.')
                }
            }).then(() => {
                this.confirmingSave = false
                if (!this.showProgressModal) {
                    this.showPreviewModal = false
                }
            })
        },
        getAddendumSequence(addendum) {
            if (!addendum) {
                return 1
            }

            const baseList = (this.contractAddendums && this.contractAddendums.length > 0)
                ? this.contractAddendums
                : (this.addendum && this.addendum.id === addendum.id ? [this.addendum] : [])

            if (!baseList || baseList.length === 0) {
                return 1
            }

            const sorted = [...baseList].sort((a, b) => Number(a.id) - Number(b.id))
            const index = sorted.findIndex(item => Number(item.id) === Number(addendum.id))
            return index >= 0 ? index + 1 : 1
        },
        processAddendum() {
            this.loading = true
            const removedDepositProducts = (this.addendum?.items || [])
                .filter(item => item.action === 'remove' && this.parseDecimal(item.deposit || 0) > 0)
                .map(item => ({
                    id: Number(item.product || item.id),
                    product: Number(item.product || item.id),
                    name: item.name,
                    amount: Number(item.amount || 1),
                    price: this.parseDecimal(item.price || 0),
                    deposit: this.parseDecimal(item.deposit || 0),
                    btw: this.parseDecimal(item.btw || 0),
                    type: item.type
                }))
            axios.post(`/api/contract/addendums/${this.addendum.id}/process`, {}, {
                headers: { 'Authorization': 'bearer ' + this.$store.state.authToken }
            }).then((response) => {
                const cronjobIds = response.data?.cronjob_ids || []
                if (response.data?.deferred) {
                    this.showMessage('Contractaanpassing blijft ingepland voor verwerking op de ingangsdatum.')
                    this.fetchAddendum()
                    return
                }
                if (cronjobIds.length > 0) {
                    this.showMessage('Contractaanpassing verwerkt. Facturen worden direct gegenereerd.')
                    if (removedDepositProducts.length > 0) {
                        this.startCronjobProcessing(cronjobIds, 'none', false)
                        this.openCreditModalForRemovedProducts(removedDepositProducts)
                    } else {
                        this.startCronjobProcessing(cronjobIds, 'refresh')
                    }
                } else {
                    this.showMessage('Contractaanpassing verwerkt.')
                    if (removedDepositProducts.length > 0) {
                        this.openCreditModalForRemovedProducts(removedDepositProducts)
                    } else {
                        this.fetchAddendum()
                    }
                }
            }).catch((error) => {
                if (error?.response?.data?.error === 'addendum-not-signed') {
                    this.showError('Deze contractaanpassing kan pas verwerkt worden na ondertekening.')
                } else {
                    this.showError('Contractaanpassing verwerken is mislukt.')
                }
            }).then(() => {
                this.loading = false
            })
        },
        startCronjobProcessing(cronjobIds, afterAction = 'redirect', showModal = true) {
            this.cronjobIds = cronjobIds
            this.totalCronjobs = cronjobIds.length
            this.currentCronjobIndex = 0
            this.cronjobsCompleted = false
            this.cronjobErrors = []
            this.cronjobAfterAction = afterAction
            this.showProgressModal = showModal
            this.processCronjobs()
        },
        async processCronjobs() {
            this.processingCronjobs = true

            for (let i = 0; i < this.cronjobIds.length; i++) {
                this.currentCronjobIndex = i + 1

                try {
                    await axios.post(`/api/cronjob/${this.cronjobIds[i]}/execute`, {}, {
                        headers: { 'Authorization': 'bearer ' + this.$store.state.authToken }
                    })
                } catch (error) {
                    this.cronjobErrors.push({
                        id: this.cronjobIds[i],
                        error: error?.response?.data?.error || 'Unknown error'
                    })
                }

                if (i < this.cronjobIds.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 100))
                }
            }

            this.processingCronjobs = false
            this.cronjobsCompleted = true

            if (!this.showProgressModal && this.cronjobAfterAction && this.cronjobAfterAction !== 'none') {
                this.finalizeCronjobAfterAction()
            }
        },
        // leaveProcessing() {
        //     this.showProgressModal = false
        //     this.showMessage('Facturen worden op de achtergrond verwerkt.')
        //     this.finalizeCronjobAfterAction()
        // },
        finishProcessing() {
            this.showProgressModal = false
            this.finalizeCronjobAfterAction()
        },
        finalizeCronjobAfterAction() {
            if (this.cronjobAfterAction === 'refresh') {
                this.fetchAddendum()
                return
            }

            this.$router.push({ name: 'ContractsRecordView', params: { id: this.contractId } })
        },
        resendSigningEmail() {
            this.loading = true
            axios.post(`/api/contract/addendums/${this.addendum.id}/send-signing-email`, {}, {
                headers: { 'Authorization': 'bearer ' + this.$store.state.authToken }
            }).then(() => {
                this.showMessage('Ondertekeningsmail is opnieuw ingepland voor verzending.')
                this.fetchAddendum()
            }).catch(() => {
                this.showError('Ondertekeningsmail kon niet ingepland worden.')
            }).then(() => {
                this.loading = false
            })
        },
        promptCancelAddendum() {
            if (!this.addendum || !['pending_signature', 'pending_processing'].includes(this.addendum.status)) {
                return
            }

            this.$buefy.dialog.confirm({
                title: 'Contractaanpassing annuleren',
                message: 'Weet je zeker dat je deze contractaanpassing wilt annuleren? Geplande facturen worden opnieuw gegenereerd.',
                confirmText: 'Ja, annuleren',
                cancelText: 'Nee',
                type: 'is-danger',
                hasIcon: true,
                onConfirm: () => {
                    this.cancelAddendum()
                }
            })
        },
        cancelAddendum() {
            if (!this.addendum || !['pending_signature', 'pending_processing'].includes(this.addendum.status)) {
                return
            }

            this.loading = true
            axios.post(`/api/contract/addendums/${this.addendum.id}/cancel`, {}, {
                headers: { 'Authorization': 'bearer ' + this.$store.state.authToken }
            }).then((response) => {
                const invoiceCronjobIds = response.data?.invoice_cronjob_ids || []
                this.showMessage('Contractaanpassing geannuleerd. De klant ontvangt hiervan een e-mail.')

                if (invoiceCronjobIds.length > 0) {
                    this.startCronjobProcessing(invoiceCronjobIds, 'refresh')
                    return
                }

                this.fetchAddendum()
            }).catch(() => {
                this.showError('Contractaanpassing kon niet geannuleerd worden.')
            }).then(() => {
                this.loading = false
            })
        },
        itemActionLabel(action) {
            if (action === 'add') return 'Toevoegen'
            if (action === 'remove') return 'Verwijderen'
            if (action === 'update') return 'Aanpassen'
            return action
        },
        invoiceFreqLabel(value) {
            const labels = {
                week_1: 'Wekelijks',
                week_2: 'Elke 2 weken',
                week_3: 'Elke 3 weken',
                week_4: 'Elke 4 weken',
                week_5: 'Elke 5 weken',
                month: 'Maandelijks',
                quarter: 'Per kwartaal',
                halfyear: 'Per halfjaar',
                year: 'Per jaar'
            }
            return labels[value] || value
        },
        paymentTypeLabel(value) {
            return value === 'incasso' ? 'Automatische incasso' : (value === 'manual' ? 'Handmatig' : value)
        },
        addendumItemDiscountLabel(item) {
            const discounts = this.normalizeDiscounts(item?.meta?.discounts || [])
            if (discounts.length === 0) {
                return '-'
            }

            if (discounts.length === 1) {
                const discount = discounts[0]
                return discount.type === 'percentage'
                    ? `${Number(discount.value)}%`
                    : `€ ${Number(discount.value).toFixed(2)}`
            }

            return `${discounts.length} kortingen`
        },
        addendumStatusLabel(status) {
            if (status === 'pending_signature') return 'Wachten op bevestiging'
            if (status === 'pending_processing') return 'Klaar voor verwerking'
            if (status === 'applied') return 'Gereed'
            if (status === 'cancelled') return 'Geannuleerd'
            return status
        }
    }
}
</script>

<style scoped>

.formfooter .is-flex > .button + .button {
    margin-left: 0.5rem;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
